import React from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { NavMenu } from '@shopify/app-bridge-react';
import { useI18n } from '@shopify/react-i18n';
import enTranslations from './components/AppShell/translations/en.json';
import frTranslations from './components/AppShell/translations/fr.json';
import AppShell from './components/AppShell';
import Dashboard from './views/Dashboard';
import Feed from './views/Feed';
import Tracking from './views/Tracking';
import PendingApproval from './views/PendingApproval';
import OAuthRequired from './views/OAuthRequired';
import { CurrencyProvider } from './contexts/CurrencyContext';

const translationsByLocale = { en: enTranslations, fr: frTranslations };

function NotFound() {
  const navigate = useNavigate();
  const [i18n] = useI18n({
    id: 'AppShell',
    fallback: enTranslations,
    translations(locale) {
      return translationsByLocale[locale] ?? enTranslations;
    },
  });

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: '16px',
      textAlign: 'center',
      padding: '32px 24px',
    }}>
      <s-text variant="headingLg">{i18n.translate('AppShell.notFound.title')}</s-text>
      <s-text tone="subdued">{i18n.translate('AppShell.notFound.description')}</s-text>
      <s-button variant="primary" onClick={() => navigate('/')}>
        {i18n.translate('AppShell.notFound.backButton')}
      </s-button>
    </div>
  );
}

export default function App() {
  const [i18n] = useI18n({
    id: 'AppShell',
    fallback: enTranslations,
    translations(locale) {
      return translationsByLocale[locale] ?? enTranslations;
    },
  });

  // Flags injected by the server into index.html at load time (no async race conditions).
  // __MDF_NEEDS_OAUTH__  = true  → no access token, must run OAuth before anything else
  // __MDF_IS_APPROVED__  = false → store explicitly blocked by MDF (manual DB override)
  if (window.__MDF_NEEDS_OAUTH__ === true) {
    return <OAuthRequired />;
  }

  if (window.__MDF_IS_APPROVED__ === false) {
    return <PendingApproval />;
  }

  return (
    <CurrencyProvider>
      <AppShell>
        <NavMenu>
          <a href="/" rel="home">{i18n.translate('AppShell.nav.dashboard')}</a>
          <a href="/feed">{i18n.translate('AppShell.nav.feed')}</a>
          <a href="/tracking">{i18n.translate('AppShell.nav.tracking')}</a>
        </NavMenu>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/tracking" element={<Tracking />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AppShell>
    </CurrencyProvider>
  );
}
